import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { CrmNoteType } from '@plato/data/prisma';
import { CustomerIcon } from '@plato/ui-legacy/assets/icons/customer-icon';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { DatePicker } from '@plato/ui-legacy/components/ui/date-picker';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import Text from '@plato/ui-legacy/components/ui/text';
import TimePicker from '@plato/ui-legacy/components/ui/time-picker';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { useDiscardChangesWarning } from '@plato/ui-legacy/lib/hooks/use-discard-changes-warning';
import { appendPlainTextToLexical } from '@plato/ui-legacy/lib/rich-text/append-plain-text-to-lexical';
import { getTextContent } from '@plato/ui-legacy/lib/rich-text/get-text-content';
import { Expandable, ExpandableContent, ExpandableToggle } from '@plato/ui/components/expandable';
import { Dialog } from '@plato/ui/containers/dialog';
import {
    differenceInCalendarDays,
    format,
    formatISO,
    intlFormat,
    isToday,
    min,
    parseISO,
    roundToNearestMinutes,
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { AlignLeftIcon, CalendarIcon, CalendarPlus, ClockIcon, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import type { CrmEventDTO } from '../../../server/dto/crm-event-dto.ts';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import type { UpsertInsInsightOutcomeDTO } from '../../../server/dto/ins-insight-outcome-dto.ts';
import type { CrmTaskUpdateWithId } from '../../../server/queries/crm-log-interaction-create.ts';
import type { CrmNoteCreateInput } from '../../../server/queries/crm-note-create.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AnalyticsEventPayload, LogInteractionContext } from '../../analytics/types/analytics-events.ts';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { CustomerSelect } from '../../shared/components/form-items-with-fetch/customer-select.tsx';
import { useLogInteractionInsights } from '../hooks/use-log-interaction-insights.ts';
import { useLogInteractionTasks } from '../hooks/use-log-interaction-tasks.ts';
import type { StagedTaskSource } from '../log-interaction-dialog-context.ts';
import { LogInteractionDialogProvider } from '../log-interaction-dialog-provider.tsx';
import { LogInteractionInsightList } from './log-interaction-insight-list.tsx';
import { LogInteractionOfferList } from './log-interaction-offer-list.tsx';
import { LogInteractionTaskList } from './log-interaction-task-list.tsx';
import { NoteFormSection } from './note-form-section.tsx';
import { NoteTypeSelector } from './note-type-selector.tsx';
import { SwapSlot } from './swap-slot.tsx';

type LogInteractionFormProps = {
    customer?: CustomerDTO;
    event?: CrmEventDTO;
    onCancel?: () => void;
    onSuccess?: () => void;
    onIsDirtyChange?: (isDirty: boolean) => void;
};

type LogInteractionFormInnerProps = LogInteractionFormProps & {
    /** Opened by the dialog above, which also sends the discard event with it. */
    logSession: LogInteractionContext;
};

export type LogInteractionFormValues = Omit<CrmNoteCreateInput, 'noteType'> & {
    taskUpdates: CrmTaskUpdateWithId[];
    newTasks: CrmTaskInputDTO[];
    insInsightOutcomes: UpsertInsInsightOutcomeDTO[];
};

function getMaxDate(): Date {
    return roundToNearestMinutes(new Date(), { roundingMethod: 'floor', nearestTo: 15 });
}

function getDefaultOccurredAt(event: CrmEventDTO | undefined): string {
    if (!event) {
        return getMaxDate().toISOString();
    }

    return getOccurredAtFromEvent(event);
}

function getOccurredAtFromEvent(event: CrmEventDTO): string {
    return fromZonedTime(`${event.date}T${event.start_time ?? '12:00:00'}`, 'Europe/Berlin').toISOString();
}

export function LogInteractionForm({
    customer,
    event,
    onSuccess,
    onCancel,
    onIsDirtyChange,
    logSession,
}: LogInteractionFormInnerProps): ReactNode {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const { toast } = useToast();
    const navigate = useNavigate();
    const utils = api.useUtils();
    const { currentSessionId } = useVoiceRecording();
    const { enableCreateNextEventFakeDoor, userId } = useAppConfig();
    const [noteType, setNoteType] = useState<CrmNoteType>('VISIT');

    const sendEvent = useAnalytics();
    const [aiSessionId, setAISessionId] = useState<string | undefined>(undefined);
    const [suggestionDismissed, setSuggestionDismissed] = useState(false);
    const [pickedCustomerName, setPickedCustomerName] = useState<string | undefined>(customer?.customerName);

    const handleNoteTypeChange = (value: CrmNoteType): void => {
        setNoteType(value);
        setValue('interactionHappened', value === 'NOTE' ? null : true, { shouldDirty: true });
        sendEvent('log_interaction_type_selected', { ...logSession, note_type: value });
    };

    const dismissSuggestion = (): void => {
        setSuggestionDismissed(true);
        setValue('occurredAt', '', { shouldDirty: true });
        setValue('eventId', undefined, { shouldDirty: true });
    };
    const reapplySuggestion = (): void => {
        setSuggestionDismissed(false);
    };

    const applyNow = (): void => {
        setValue('occurredAt', getMaxDate().toISOString(), { shouldDirty: true });
    };

    // An agenda cleared in the editor still saves an empty document, so check its text.
    const hasRichAgenda = !!getTextContent(event?.agendaRich);

    const defaultValues = {
        title: undefined,
        customerId: customer?.customerId,
        eventId: event?.event_id,
        occurredAt: getDefaultOccurredAt(event),
        attachments: undefined,
        interactionHappened: true,
        taskUpdates: [],
        newTasks: [],
        insInsightOutcomes: [],
    };

    const form = useForm<LogInteractionFormValues>({ defaultValues });

    const {
        control,
        handleSubmit,
        clearErrors,
        watch,
        setValue,
        getValues,
        formState: { errors: formErrors, isDirty },
    } = form;

    const watchedCustomerId = watch('customerId');
    const watchedInteractionHappened = watch('interactionHappened');
    const notReachedMode = watchedInteractionHappened === false;
    const shouldSuggestDate = !event && !!watchedCustomerId && noteType !== 'NOTE';

    const { items: taskItems, addNewTasks } = useLogInteractionTasks(form, { userId });
    const { upsertInsInsightOutcome, removeInsInsightOutcome, stagedInsInsightOutcomes } =
        useLogInteractionInsights(form);

    const handleAddTasks = (tasks: CrmTaskInputDTO[], source: StagedTaskSource): void => {
        sendEvent('log_interaction_task_added', {
            ...logSession,
            task_intent: tasks[0]?.taskIntent,
            assignee_count: tasks.length,
            is_assigned_to_oneself: tasks.some((task) => task.assignedToId === userId),
            source,
        });
        addNewTasks(tasks);
    };

    const lastEventQuery = api.platoUser.query.events.getLastUnloggedCustomerEvent.useQuery(
        {
            customerId: watchedCustomerId,
        },
        { enabled: shouldSuggestDate },
    );

    const suggestedEvent = shouldSuggestDate ? (lastEventQuery.data ?? null) : null;

    useEffect(() => {
        setSuggestionDismissed(false);
    }, [watchedCustomerId]);

    const showSuggestedEvent = !event && !!suggestedEvent && !suggestionDismissed;
    const eventDateTime = (event?: CrmEventDTO): string => {
        if (!event) {
            return '';
        }

        return intlFormat(
            new Date(getOccurredAtFromEvent(event)),
            {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            },
            { locale },
        );
    };

    useEffect(() => {
        clearErrors('title');
    }, [clearErrors]);

    useEffect(() => {
        // `logSession` is fixed for this dialog instance, so this fires once per open.
        sendEvent('log_interaction_opened', logSession);
    }, [sendEvent, logSession]);

    useEffect(() => {
        onIsDirtyChange?.(isDirty);
    }, [isDirty, onIsDirtyChange]);

    useEffect(() => {
        if (!shouldSuggestDate || !lastEventQuery.isFetched || suggestedEvent) {
            return;
        }

        sendEvent('log_date_prefill_unavailable', { customer_id: watchedCustomerId });
    }, [shouldSuggestDate, lastEventQuery.isFetched, suggestedEvent, sendEvent, watchedCustomerId]);

    // While the suggestion is the active choice, mirror it into the form so submit
    // links the suggested event (eventId) and uses its date/time (occurredAt)
    useEffect(() => {
        if (!showSuggestedEvent) {
            return;
        }
        setValue('eventId', suggestedEvent.event_id);
        setValue('occurredAt', getOccurredAtFromEvent(suggestedEvent));
    }, [showSuggestedEvent, suggestedEvent, setValue]);

    const createMutation = api.platoUser.command.logInteraction.create.useMutation({
        onSuccess: async (note, variables) => {
            // Read before the invalidation below refetches it: the staged updates carry no assignor.
            const upcomingTasks = utils.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.getData({
                customerId: variables.customerId,
            });
            const taskUpdates = variables.taskUpdates ?? [];
            // The modal only lists open tasks, so a staged COMPLETED status is always a completion.
            const completedTaskUpdates = taskUpdates.filter((update) => update.status === 'COMPLETED');

            await utils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
            await utils.platoUser.query.events.getLastUnloggedCustomerEvent.invalidate();
            // A visit logged without an event gets one on the server, so the calendar changes too.
            await utils.platoUser.query.events.getList.invalidate();
            await utils.platoUser.query.tasks.getManyTasks.getUpcomingCustomerTasks.invalidate();
            await utils.platoUser.query.tasks.getManyTasks.getListWithFilters.invalidate();

            toast({
                icon: 'success',
                variant: 'default',
                title: t('notes__form_create_toast__success_title', { ns: 'notes' }),
                createdLink: (
                    <LinkText
                        as="button"
                        onClick={() =>
                            void navigate(`/customers/${encodeURIComponent(note.customerId)}/activity`)
                        }
                    />
                ),
            });

            sendEvent('customer_interaction_created', {
                ...logSession,
                note_id: note.noteId,
                customer_id: note.customerId,
                note_type: noteType,
                ai_session_id: aiSessionId,
                not_reached_mode: notReachedMode,
                had_reason: !!note.title,
                task_count: variables.newTasks?.length ?? 0,
                task_update_count: taskUpdates.length,
                task_completed_count: completedTaskUpdates.length,
            });

            for (const update of taskUpdates) {
                const assignorId = upcomingTasks?.find((task) => task.taskId === update.taskId)?.assignedBy
                    .id;
                const relatedLink =
                    update.links.find((link) => link.modelType === 'sales_quote') ??
                    update.links.find((link) => link.modelType === 'customer') ??
                    update.links[0];
                const payload = {
                    ...logSession,
                    task_id: update.taskId,
                    due_date: update.dueDate.toISOString(),
                    assignee_id: update.assignedToId,
                    assignor_id: assignorId,
                    is_assigned_to_oneself: update.assignedToId === assignorId,
                    related_entity_type: relatedLink?.modelType,
                    related_entity_id: relatedLink?.modelId,
                    source: 'log_interaction_dialog',
                } as const;

                sendEvent('task_updated', { ...payload, changed_by_id: userId });

                if (update.status === 'COMPLETED') {
                    sendEvent('task_completed', payload);
                }
            }

            if (suggestedEvent) {
                const daysAgo = differenceInCalendarDays(new Date(), parseISO(suggestedEvent.date));

                if (suggestionDismissed) {
                    sendEvent('log_date_prefill_overridden', {
                        event_id: suggestedEvent.event_id,
                        days_ago: daysAgo,
                        fields_changed: 'both',
                    });
                } else {
                    sendEvent('log_date_prefill_used', {
                        event_id: suggestedEvent.event_id,
                        days_ago: daysAgo,
                        interaction_type: 'besuch',
                    });
                }
            }

            onSuccess?.();
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const onSubmit = handleSubmit((data) => {
        const isNote = noteType === 'NOTE';
        createMutation.mutate({
            customerId: data.customerId,
            eventId: isNote ? undefined : data.eventId,
            noteType,
            crmNote: {
                ...data,
                titleRich: data.title,
                occurredAt: isNote ? undefined : data.occurredAt,
                interactionHappened: isNote ? null : data.interactionHappened,
            },
            taskUpdates: isNote ? [] : data.taskUpdates,
            newTasks: isNote ? [] : data.newTasks,
            insInsightOutcomes: isNote ? [] : data.insInsightOutcomes,
        });
    });

    const handleCreateNextEventClick = (): void => {
        sendEvent('create_next_event_from_log', {
            customer_id: watchedCustomerId,
        });
        toast({
            icon: 'info',
            title: t('fake_door_next_event_toast_title', { ns: 'events' }),
            description: t('fake_door_next_event_toast_description', { ns: 'events' }),
        });
    };

    return (
        <DialogForm>
            <DialogForm.Header title={t('notes__form_create_title', { ns: 'notes' })} />
            <DialogForm.Content className="[@media(min-height:700px)]:min-h-125">
                {!event && <NoteTypeSelector value={noteType} onChange={handleNoteTypeChange} />}
                <DialogForm.ItemList grid>
                    {/* Customer Field */}
                    <DialogForm.Item className="items-start">
                        <DialogForm.Label
                            htmlFor={customer ? undefined : 'customerId'}
                            icon={CustomerIcon}
                            required={!customer}
                        >
                            {t('notes__form__customer_label', { ns: 'notes' })}
                        </DialogForm.Label>
                        {customer ? (
                            <DialogForm.Body className="flex flex-col py-1.5 px-2">
                                <Text color="neutral-950">{customer.customerName}</Text>
                                {customer.customerId && (
                                    <Text size="xs" color="neutral-500" className="tabular-nums">
                                        {customer.customerId}
                                    </Text>
                                )}
                            </DialogForm.Body>
                        ) : (
                            <DialogForm.Body className="flex flex-col gap-1">
                                <Controller
                                    name="customerId"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t('form__customer_required', { ns: 'common' }),
                                        },
                                    }}
                                    render={({ field: { value, onChange } }) => (
                                        <CustomerSelect
                                            value={value}
                                            onChange={(id, picked) => {
                                                onChange(id);
                                                setPickedCustomerName(picked.name);
                                            }}
                                            className="w-full"
                                            variant="inline"
                                            error={!!formErrors.customerId}
                                            tracking={{
                                                context: 'note_customer_select',
                                                context_entity_type: 'note',
                                            }}
                                        />
                                    )}
                                />
                                <DialogForm.Error error={formErrors.customerId} />
                            </DialogForm.Body>
                        )}
                    </DialogForm.Item>
                    {noteType !== 'NOTE' && (
                        <>
                            {event ? (
                                // Calendar-event entry: fixed, non-editable Termin row (no swap),
                                // followed by the event's agenda when it has one.
                                <>
                                    <DialogForm.Item className="items-start">
                                        <DialogForm.Label icon={CalendarIcon}>
                                            {t('notes__form__event_label', { ns: 'notes' })}
                                        </DialogForm.Label>
                                        <DialogForm.Body className="py-1.5 px-2">
                                            <Text color="neutral-950">{eventDateTime(event)}</Text>
                                        </DialogForm.Body>
                                    </DialogForm.Item>
                                    {(hasRichAgenda || event.agenda?.trim()) && (
                                        <DialogForm.Item className="items-start">
                                            <DialogForm.Label icon={AlignLeftIcon}>
                                                {t('notes__form__agenda_label', { ns: 'notes' })}
                                            </DialogForm.Label>
                                            <DialogForm.Body className="py-1.5 px-2">
                                                <Expandable>
                                                    <ExpandableContent>
                                                        {hasRichAgenda && event.agendaRich ? (
                                                            <RichTextViewer content={event.agendaRich} />
                                                        ) : (
                                                            <Text
                                                                color="neutral-950"
                                                                className="whitespace-pre-line"
                                                            >
                                                                {event.agenda}
                                                            </Text>
                                                        )}
                                                    </ExpandableContent>
                                                    <ExpandableToggle
                                                        expandLabel={t('show_more', { ns: 'common' })}
                                                        collapseLabel={t('show_less', { ns: 'common' })}
                                                    />
                                                </Expandable>
                                            </DialogForm.Body>
                                        </DialogForm.Item>
                                    )}
                                </>
                            ) : (
                                // Customer entry: suggested (read-only + ✕) ↔ manual (Datum/Uhrzeit
                                // + chips), cross-faded with SwapSlot on dismiss / re-apply.
                                <>
                                    {showSuggestedEvent && (
                                        <div className="col-span-2 flex justify-end">
                                            <span
                                                className="text-xs font-medium"
                                                style={{
                                                    backgroundImage:
                                                        'linear-gradient(to right, #4C74E6, #8D54E9)',
                                                    WebkitBackgroundClip: 'text',
                                                    backgroundClip: 'text',
                                                    color: 'transparent',
                                                }}
                                            >
                                                {t('notes__form__last_event', { ns: 'notes' })}
                                            </span>
                                        </div>
                                    )}

                                    {/* Stack wrapper: passes the form's [auto_1fr] columns down
                                    via subgrid so both SwapSlots align with the other rows. */}
                                    <div className="col-span-2 grid grid-cols-subgrid">
                                        <SwapSlot subgrid active={showSuggestedEvent}>
                                            <DialogForm.Label icon={CalendarIcon}>
                                                {t('notes__form__event_label', { ns: 'notes' })}
                                            </DialogForm.Label>
                                            {/* `relative` anchors the AI prefill rail to this
                                            field cell (the SwapSlot's overflow-hidden would
                                            otherwise clip a rail anchored to a far ancestor). */}
                                            <DialogForm.Body className="relative py-1.5 px-2">
                                                <>
                                                    <span
                                                        aria-hidden
                                                        className="pointer-events-none absolute inset-y-0 right-0 w-1"
                                                        style={{
                                                            background:
                                                                'linear-gradient(180deg, #A5B4FC, #C4B5FD)',
                                                        }}
                                                    />
                                                    <Text color="neutral-950">
                                                        {eventDateTime(suggestedEvent ?? undefined)}
                                                    </Text>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        icon={X}
                                                        onClick={dismissSuggestion}
                                                        aria-label={t('notes__form__date_clear', {
                                                            ns: 'notes',
                                                        })}
                                                        className="h-auto min-w-0 rounded p-0.5 text-neutral-700 hover:text-neutral-950"
                                                    />
                                                </>
                                            </DialogForm.Body>
                                        </SwapSlot>
                                        <SwapSlot subgrid active={!showSuggestedEvent}>
                                            {/* Date Field */}
                                            <DialogForm.Label icon={CalendarIcon} required>
                                                {t('notes__form__date_label', { ns: 'notes' })}
                                            </DialogForm.Label>
                                            <div className="min-w-0">
                                                <Controller
                                                    name="occurredAt"
                                                    control={control}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                            message: t('form__date_required', {
                                                                ns: 'common',
                                                            }),
                                                        },
                                                    }}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            title={t('notes__form__date_label', {
                                                                ns: 'notes',
                                                            })}
                                                            disableIcon
                                                            value={
                                                                field.value
                                                                    ? new Date(field.value)
                                                                    : undefined
                                                            }
                                                            valuePrefix=""
                                                            onChange={(value) => {
                                                                if (!value) {
                                                                    return;
                                                                }

                                                                const baseDate = field.value
                                                                    ? new Date(field.value)
                                                                    : new Date();

                                                                const timePart = formatISO(baseDate, {
                                                                    representation: 'time',
                                                                });
                                                                const datePart = formatISO(value, {
                                                                    representation: 'date',
                                                                });
                                                                const nextDate = new Date(
                                                                    datePart + 'T' + timePart,
                                                                );

                                                                field.onChange(
                                                                    min([
                                                                        getMaxDate(),
                                                                        nextDate,
                                                                    ]).toISOString(),
                                                                );
                                                            }}
                                                            disabled={{ after: new Date() }}
                                                            className="w-full border border-transparent py-1"
                                                        />
                                                    )}
                                                />
                                                <DialogForm.Error error={formErrors.occurredAt} />
                                            </div>

                                            {/* Time Field */}
                                            <DialogForm.Label htmlFor={'businessTime'} icon={ClockIcon}>
                                                {t('notes__form__time_label', { ns: 'notes' })}
                                            </DialogForm.Label>
                                            <div className="min-w-0">
                                                <Controller
                                                    name="occurredAt"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TimePicker
                                                            value={
                                                                field.value
                                                                    ? format(
                                                                          new Date(field.value),
                                                                          'HH:mm:ss',
                                                                      )
                                                                    : null
                                                            }
                                                            onChange={(value) => {
                                                                const baseDate = field.value
                                                                    ? new Date(field.value)
                                                                    : new Date();

                                                                const datePart = formatISO(baseDate, {
                                                                    representation: 'date',
                                                                });
                                                                const nextDate = new Date(
                                                                    datePart + 'T' + value,
                                                                );

                                                                field.onChange(nextDate.toISOString());
                                                            }}
                                                            max={
                                                                !field.value || isToday(field.value)
                                                                    ? format(getMaxDate(), 'HH:mm:ss')
                                                                    : undefined
                                                            }
                                                            placeholder={t('time_picker__placeholder', {
                                                                ns: 'common',
                                                            })}
                                                            className="w-full border border-transparent py-1"
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* Jetzt + letzter Termin chips */}
                                            {suggestedEvent && (
                                                <div className="col-span-2 flex flex-wrap items-center gap-2">
                                                    {suggestionDismissed && (
                                                        <Button
                                                            type="button"
                                                            variant="subtle"
                                                            rounded
                                                            onClick={applyNow}
                                                            className="h-auto gap-1.5 bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                                                        >
                                                            {t('notes__form__date_now', { ns: 'notes' })}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        rounded
                                                        leftIcon={CalendarIcon}
                                                        onClick={reapplySuggestion}
                                                        className="h-auto gap-1.5 bg-violet-100 px-3 py-1.5 text-sm text-neutral-900 hover:bg-violet-200"
                                                    >
                                                        {t('notes__form__date_prefill_source', {
                                                            ns: 'notes',
                                                        })}
                                                    </Button>
                                                </div>
                                            )}
                                        </SwapSlot>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </DialogForm.ItemList>
                <NoteFormSection
                    noteType={noteType}
                    control={control}
                    errors={formErrors}
                    onImproveTextAccepted={setAISessionId}
                    onVoiceTranscription={(text) => {
                        const next = appendPlainTextToLexical(getValues('title'), text);
                        setValue('title', next, { shouldDirty: true });
                    }}
                />
                {noteType !== 'NOTE' && watchedCustomerId && (
                    <LogInteractionDialogProvider
                        session={logSession}
                        customerId={watchedCustomerId}
                        customerName={pickedCustomerName}
                        stagedTasks={taskItems}
                        addTasks={handleAddTasks}
                        stagedInsInsightOutcomes={stagedInsInsightOutcomes}
                        upsertInsInsightOutcome={upsertInsInsightOutcome}
                        removeInsInsightOutcome={removeInsInsightOutcome}
                    >
                        <LogInteractionOfferList />
                        <LogInteractionTaskList />
                        <LogInteractionInsightList />
                    </LogInteractionDialogProvider>
                )}
                {noteType !== 'NOTE' && enableCreateNextEventFakeDoor && (
                    <div className="self-start">
                        <Button variant="secondary" icon={CalendarPlus} onClick={handleCreateNextEventClick}>
                            {t('fake_door_next_event_button', { ns: 'events' })}
                        </Button>
                    </div>
                )}
            </DialogForm.Content>
            <DialogForm.SubmitBlock
                onCancel={onCancel}
                onSubmit={() => {
                    void onSubmit();
                }}
                submitText={notReachedMode ? t('notes__not_reached__submit', { ns: 'notes' }) : undefined}
                disabled={createMutation.isPending || !!currentSessionId}
            />
        </DialogForm>
    );
}

export type LogInteractionDialogProps = ModalInnerProps &
    LogInteractionFormProps & {
        analyticsSource?: AnalyticsEventPayload<'customer_interaction_created'>['page_source'];
    };

export const LogInteractionDialog = DialogStateManager.create<LogInteractionDialogProps>(
    ({ onSuccess, ...props }) => {
        const { visible, remove } = useDialog();
        const [isDirty, setIsDirty] = useState(false);
        const { confirmDiscard } = useDiscardChangesWarning(isDirty);
        const sendEvent = useAnalytics();

        // Minted here, not in the form, so the discard below carries the same id as the actions
        // inside it. State, not a memo: the open event keys off this object's identity.
        const [session] = useState<LogInteractionContext>(() => ({
            log_session_id: uuid(),
            page_source: props.analyticsSource,
        }));

        const handleClose = (): void => {
            confirmDiscard(() => {
                sendEvent('log_interaction_discarded', { ...session, had_content: isDirty });
                remove();
            });
        };

        return (
            <Dialog open={visible} onClose={handleClose} disablePointerDismissal>
                <LogInteractionForm
                    onCancel={handleClose}
                    onSuccess={() => {
                        remove();
                        onSuccess?.();
                    }}
                    onIsDirtyChange={setIsDirty}
                    logSession={session}
                    {...props}
                />
            </Dialog>
        );
    },
);
